// =============================================================================
// CostAnalytics — sub-tab inside local-tickets section
// =============================================================================
// Shows per-call LLM cost data sourced from CallSession entity.
//   - Recent call sessions list (sorted by endedAt DESC)
//   - Cost per call (USD + EUR), token usage, outcome, linked ticket
//
// Source of truth: CallSession entity (written on call-end / TTL flush).
//
// Note: aggregate KPI cards (today/7d/30d totals) deferred until we add
// server-side aggregation via baseql; for MVP, view the list directly.
// =============================================================================

import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { DataTable } from '@adornis/data-view/client/table/data-table.js';
import { View } from '@adornis/data-view/db/view.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { format } from 'date-fns';
import { html } from 'lit';
import { CallSession } from '../../db/CallSession.js';

// ────────────────────────────────────────────────────────────────
// Outcome badge
// ────────────────────────────────────────────────────────────────
const outcomeBadge = (outcome: string | null | undefined) => {
  if (!outcome) return html`<span class="badge badge-ghost">—</span>`;
  const cls =
    outcome === 'in-progress'
      ? 'badge-info'
      : outcome === 'ticket-created'
        ? 'badge-success'
        : outcome === 'redirected'
          ? 'badge-warning'
          : outcome === 'abandoned'
            ? 'badge-error'
            : 'badge-ghost';
  return html`<span class="badge ${cls}">${outcome}</span>`;
};

// ────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────
export const CostAnalytics = stateful(() => {
  const [searchQuery, setSearchQuery] = useState('');

  // Data table — DataTable handles the subscription internally via dataViewOptions
  const dataViewOptions = buildFullOptions({
    class: CallSession,
    isSortable: false,
    customColumns: [
      // Condensed cost-focused layout — fits without horizontal scroll (~820px).
      // Phone/Lang/Outcome/tokens/Ticket removed to make room for the cost breakdown;
      // search still covers phone/sessionID/agent/outcome via searchFields below.
      {
        fieldName: 'endedAt',
        columnName: 'Zeit',
        columnWidth: '125px',
        renderItem: (s: CallSession) => (s.endedAt ? format(new Date(s.endedAt), 'dd.MM HH:mm') : '—'),
      },
      { fieldName: 'finalAgent', columnName: 'Agent', columnWidth: '120px' },
      { fieldName: 'totalTurns', columnName: 'Turns', columnWidth: '60px' },
      {
        fieldName: 'totalCostEUR',
        columnName: 'LLM',
        columnWidth: '78px',
        renderItem: (s: CallSession) => (typeof s.totalCostEUR === 'number' ? `€${s.totalCostEUR.toFixed(4)}` : '—'),
      },
      {
        fieldName: 'costTwilioEUR',
        columnName: 'Twilio',
        columnWidth: '78px',
        renderItem: (s: CallSession) => (typeof s.costTwilioEUR === 'number' ? `€${s.costTwilioEUR.toFixed(4)}` : '—'),
      },
      {
        fieldName: 'costDeepgramEUR',
        columnName: 'Deepgram',
        columnWidth: '85px',
        renderItem: (s: CallSession) =>
          typeof s.costDeepgramEUR === 'number' ? `€${s.costDeepgramEUR.toFixed(4)}` : '—',
      },
      {
        fieldName: 'costCartesiaEUR',
        columnName: 'Cartesia',
        columnWidth: '82px',
        renderItem: (s: CallSession) =>
          typeof s.costCartesiaEUR === 'number' ? `€${s.costCartesiaEUR.toFixed(4)}` : '—',
      },
      {
        fieldName: 'costTotalEUR',
        columnName: 'Gesamt',
        columnWidth: '92px',
        renderItem: (s: CallSession) =>
          typeof s.costTotalEUR === 'number'
            ? html`<span class="font-bold text-primary">€${s.costTotalEUR.toFixed(4)}</span>`
            : '—',
      },
      {
        fieldName: 'estimationMethod',
        columnName: 'Typ',
        columnWidth: '95px',
        renderItem: (s: CallSession) => {
          const m = s.estimationMethod ?? '';
          if (m.startsWith('provider-exact')) return html`<span class="badge badge-success badge-sm">Exakt</span>`;
          if (m.startsWith('retokenized')) return html`<span class="badge badge-info badge-sm">Prognose+</span>`;
          return html`<span class="badge badge-ghost badge-sm">Prognose</span>`;
        },
      },
    ],
    view: new View({
      filter: new AdornisFilter({
        sortingFields: ['endedAt'],
        sortingDirections: ['desc'],
        search: searchQuery || null,
        searchFields: ['phoneNumber', 'sessionID', 'callUuid', 'linkedTicketNumber', 'finalAgent', 'outcome'],
        // NOTE: Boolean test-call exclusion removed — @adornis/baseql@18.2.6 schema
        // registers ONLY String aspect inputs (no BooleanAspectInput), so the boolean
        // filter crashed the subscription (NEW-3). Test calls record no cost (€0) so they
        // appear harmlessly; clean server-side exclusion returns with the real-usage resolver.
        aspects: [],
      }),
    }),
  });

  return html`
    <div class="card bg-base-100 m-4">
      <div class="card-body p-8">
        <div class="flex flex-row justify-between space-x-4 mb-4">
          <h2 class="card-title text-2xl font-bold text-primary">Cost Analytics — Prognose (Schätzung)</h2>
          <label class="input input-bordered flex items-center gap-2 min-w-xl">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="w-4 h-4 opacity-70 flex-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
              />
            </svg>
            <input
              type="text"
              class="grow"
              placeholder="Search sessions (phone, UUID, ticket, agent)…"
              @input=${(e: Event) => setSearchQuery((e.target as HTMLInputElement).value)}
            />
          </label>
        </div>

        <!-- Estimation notice -->
        <div class="alert alert-info mb-4">
          <div class="text-sm">
            <strong>⚠ Prognose / Schätzung — keine echte Abrechnung.</strong> Kosten basieren auf Mistral-Large-Preisen
            ($2/M Input, $6/M Output) und char-basierter Token-Schätzung (±10% vs. tatsächliche Abrechnung). Die Raten
            sind in Settings unter „Kosten → Preise" editierbar. Output-Tokens werden derzeit geschätzt; tatsächliche
            Werte folgen nach Verkabelung der Streaming-Usage-Captures. KPI-Aggregate (heute / 7T / 30T) folgen mit
            Server-Side-Aggregation.
          </div>
        </div>

        <!-- Sessions table -->
        <div>${DataTable({ options: dataViewOptions.dataViewOptions })}</div>
      </div>
    </div>
  `;
});
